"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { appendQueryFlag, resolveTypeAheadUrl } from "../constants/typeahead-endpoints.js";
function debounce(fn, wait) {
    let timeoutId = null;
    const debounced = (query)=>{
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(()=>{
            timeoutId = null;
            fn(query);
        }, wait);
    };
    debounced.cancel = ()=>{
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = null;
    };
    return debounced;
}
function isAbortError(error) {
    return 'object' == typeof error && null !== error && 'name' in error && 'AbortError' === error.name;
}
function useTypeAheadSearch({ searchValue, typeAheadEndpoint, config, debounceTime = 100, airportNestingEnabled = false, neighbourhoodNestingEnabled = false, hasAISearch = false, hasLTRSearch = false, isMobile = false, onLoaded, onLtrSearch, onResultCity }) {
    const [state, setState] = useState({
        loading: false,
        error: null,
        items: [],
        query: ''
    });
    const prevSearchValueRef = useRef(null);
    const onLoadedRef = useRef(onLoaded);
    const onLtrSearchRef = useRef(onLtrSearch);
    const onResultCityRef = useRef(onResultCity);
    useEffect(()=>{
        onLoadedRef.current = onLoaded;
        onLtrSearchRef.current = onLtrSearch;
        onResultCityRef.current = onResultCity;
    }, [
        onLoaded,
        onLtrSearch,
        onResultCity
    ]);
    const mountedRef = useRef(false);
    useEffect(()=>{
        mountedRef.current = true;
        return ()=>{
            mountedRef.current = false;
        };
    }, []);
    const abortControllerRef = useRef(null);
    const abortCurrentRequest = useCallback(()=>{
        abortControllerRef.current?.abort();
        abortControllerRef.current = null;
    }, []);
    const configRef = useRef(config);
    useEffect(()=>{
        configRef.current = config;
    }, [
        config
    ]);
    const fetchTypeaheadData = useCallback((query)=>{
        abortCurrentRequest();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        let url = resolveTypeAheadUrl({
            endpointKey: typeAheadEndpoint,
            query,
            config: configRef.current
        });
        if (hasAISearch) url = appendQueryFlag(url, 'searchModes=ai-search');
        if (hasLTRSearch) {
            url = appendQueryFlag(url, 'rankBy=RELEVANCE');
            url = appendQueryFlag(url, `appc=${isMobile ? 'MOBILEWEB' : 'DESKTOP'}`);
        }
        if (airportNestingEnabled) url = appendQueryFlag(url, 'airportNestingEnabled=true');
        else if (neighbourhoodNestingEnabled && 'hotelsV2' === typeAheadEndpoint) url = appendQueryFlag(url, 'nestingEnabled=true');
        setState((prev)=>({
                ...prev,
                loading: true,
                error: null
            }));
        const isCurrent = ()=>mountedRef.current && abortControllerRef.current === controller && !controller.signal.aborted;
        fetch(url, {
            signal: controller.signal
        }).then((res)=>res.json()).then(({ searchItems, duration, source, meta })=>{
            if (!isCurrent()) return;
            const safeItems = searchItems ?? [];
            if (hasLTRSearch) onLtrSearchRef.current?.({
                searchQuery: query,
                searchQueryLength: query.length,
                rankBy: meta?.rankBy
            });
            setState((prev)=>({
                    ...prev,
                    loading: false,
                    error: null,
                    items: safeItems,
                    query,
                    endpoint: typeAheadEndpoint,
                    duration,
                    source
                }));
            onLoadedRef.current?.();
            onResultCityRef.current?.(safeItems[0]?.cityID);
        }).catch((error)=>{
            if (isAbortError(error) || !isCurrent()) return;
            if (hasLTRSearch) onLtrSearchRef.current?.({
                searchQuery: query,
                searchQueryLength: query.length
            });
            setState((prev)=>({
                    ...prev,
                    loading: false,
                    error,
                    query,
                    endpoint: typeAheadEndpoint
                }));
            onLoadedRef.current?.();
        });
    }, [
        abortCurrentRequest,
        typeAheadEndpoint,
        airportNestingEnabled,
        neighbourhoodNestingEnabled,
        hasAISearch,
        hasLTRSearch,
        isMobile
    ]);
    const debouncedFetchRef = useRef(null);
    useEffect(()=>{
        debouncedFetchRef.current?.cancel();
        debouncedFetchRef.current = debounce((query)=>{
            fetchTypeaheadData(query);
        }, debounceTime);
        return ()=>{
            debouncedFetchRef.current?.cancel();
            abortCurrentRequest();
        };
    }, [
        abortCurrentRequest,
        fetchTypeaheadData,
        debounceTime
    ]);
    useEffect(()=>{
        if (prevSearchValueRef.current === searchValue) return;
        prevSearchValueRef.current = searchValue;
        abortCurrentRequest();
        const trimmed = searchValue?.trim();
        if (trimmed) debouncedFetchRef.current?.(trimmed);
    }, [
        abortCurrentRequest,
        searchValue
    ]);
    const prevRefetchTriggersRef = useRef({
        airportNestingEnabled,
        neighbourhoodNestingEnabled,
        typeAheadEndpoint
    });
    useEffect(()=>{
        const triggersChanged = prevRefetchTriggersRef.current.airportNestingEnabled !== airportNestingEnabled || prevRefetchTriggersRef.current.neighbourhoodNestingEnabled !== neighbourhoodNestingEnabled || prevRefetchTriggersRef.current.typeAheadEndpoint !== typeAheadEndpoint;
        prevRefetchTriggersRef.current = {
            airportNestingEnabled,
            neighbourhoodNestingEnabled,
            typeAheadEndpoint
        };
        if (!triggersChanged) return;
        abortCurrentRequest();
        const trimmed = searchValue?.trim();
        if (trimmed) debouncedFetchRef.current?.(trimmed);
    }, [
        abortCurrentRequest,
        airportNestingEnabled,
        neighbourhoodNestingEnabled,
        typeAheadEndpoint,
        searchValue
    ]);
    const search = useCallback((value)=>{
        const trimmed = value.trim();
        if (!trimmed) return;
        abortCurrentRequest();
        debouncedFetchRef.current?.(trimmed);
    }, [
        abortCurrentRequest
    ]);
    const isEmpty = !searchValue?.trim();
    const isStale = state.query !== searchValue?.trim() || state.endpoint !== typeAheadEndpoint;
    return {
        loading: isEmpty ? false : isStale ? true : state.loading,
        error: isEmpty || isStale ? null : state.error,
        items: isEmpty || isStale ? [] : state.items,
        duration: isEmpty || isStale ? void 0 : state.duration,
        source: isEmpty || isStale ? void 0 : state.source,
        search
    };
}
export { useTypeAheadSearch };
